import React, { useEffect, useState } from 'react';
import '../CSS/ReportsGrid.css';
import goalIcon from '../Images/goal.svg';

const ReportsGrid = ({
  searchTerm,
  selectedSector,
  groupResponsible,
  targetedEntity,
  selectedDropdownFilter,
  startDate,
  endDate,
  selectedIndustryFilter,
  selectedRegion,
  cityFilter,
  stateFilter,
  countryFilter,
  prio3Filter, // ✅ NEW PROP
}) => {
  const [reports, setReports] = useState([]);
  const [orgs, setOrgs] = useState([]);

  useEffect(() => {
    fetch('/api/hello')
      .then((response) => response.json())
      .then((data) => {
        const filteredReports = data.reports.filter(
          (report) =>
            report.fields.field_9 !== 'Protest Planned' &&
            report.fields.DM !== true
        );
        console.log(data);
        setReports(filteredReports);
        setOrgs(data.orgs);
      })
      .catch((error) => {
        console.error('Error fetching reports data:', error);
      });
  }, []);

  const filterReports = (
    reports,
    searchTerm,
    selectedSector,
    groupResponsible,
    targetedEntity,
    selectedDropdownFilter,
    startDate,
    endDate,
    selectedIndustryFilter,
    selectedRegion,
    cityFilter,
    stateFilter,
    countryFilter,
    prio3Filter,
    orgs
  ) => {
    const groupResponsibleLower = groupResponsible?.toLowerCase();
    let relatedGroupNames = new Set();

    const start = startDate ? new Date(startDate) : null;
    const end = endDate ? new Date(endDate) : null;

    const cityLower = cityFilter?.trim().toLowerCase();
    const stateLower = stateFilter?.trim().toLowerCase();
    const countryLower = countryFilter?.trim().toLowerCase();

    if (groupResponsibleLower && orgs.length > 0) {
      orgs.forEach((org) => {
        const title = org.fields?.Title?.toLowerCase();
        const aka = org.fields?.AKA?.toLowerCase();
        const oldName = org.fields?.OldName?.toLowerCase();

        if (
          title?.includes(groupResponsibleLower) ||
          aka?.includes(groupResponsibleLower) ||
          oldName?.includes(groupResponsibleLower)
        ) {
          if (title) relatedGroupNames.add(title);
          if (aka) relatedGroupNames.add(aka);
          if (oldName) relatedGroupNames.add(oldName);
        }
      });

      if (relatedGroupNames.size === 0) {
        relatedGroupNames.add(groupResponsibleLower);
      }
    }

    return (reports || []).filter((report) => {
      if (!report.fields) return false;

      /* -------------------------------
         ✅ Prio3 Filter Logic
      -------------------------------- */
      const isPrio3 = report.fields.Prio3 === true;

      if (prio3Filter === 'hide' && isPrio3) return false;
      if (prio3Filter === 'only' && !isPrio3) return false;
      // 'all' → no restriction

      const sector = report.fields.field_6 || [];
      const searchTermLower = searchTerm?.toLowerCase();

      const titleMatches =
        !searchTerm ||
        report.fields.Title?.toLowerCase().includes(searchTermLower);

      const groupResponsibleMatches =
        !groupResponsible ||
        [...relatedGroupNames].some((name) => {
          const nameLower = name.toLowerCase();
          return (
            report.fields.field_10?.toLowerCase().includes(nameLower) ||
            (Array.isArray(report.fields.GroupResponsibleLU) &&
              report.fields.GroupResponsibleLU.some((item) =>
                item.LookupValue?.toLowerCase().includes(nameLower)
              ))
          );
        });

      const targetedEntityMatches =
        !targetedEntity ||
        report.fields.field_11
          ?.toLowerCase()
          .includes(targetedEntity.toLowerCase());

      const dropdownFilterMatches =
        !selectedDropdownFilter ||
        report.fields.field_9
          ?.toLowerCase()
          .includes(selectedDropdownFilter.toLowerCase());

      const regionMatches =
        !selectedRegion ||
        report.fields.Region0 === selectedRegion;

      const cityMatches =
        !cityLower ||
        report.fields.field_5?.toLowerCase().includes(cityLower);

      const stateMatches =
        !stateLower ||
        report.fields.field_4?.toLowerCase().includes(stateLower);

      const countryMatches =
        !countryLower ||
        report.fields.field_3?.toLowerCase().includes(countryLower);

      const reportStartRaw = report.fields.field_2_formatted;
      const reportStartDate = reportStartRaw ? new Date(reportStartRaw) : null;

      if ((start || end) && !reportStartDate) return false;

      const startDateMatches =
        !start || reportStartDate >= start;

      const endDateMatches =
        !end || reportStartDate <= end;

      const industryMatches =
        !selectedIndustryFilter ||
        report.fields.ARIndustry?.toLowerCase().includes(selectedIndustryFilter.toLowerCase()) ||
        report.fields.ESIndustry?.toLowerCase().includes(selectedIndustryFilter.toLowerCase()) ||
        report.fields.PharmaIndustry?.toLowerCase().includes(selectedIndustryFilter.toLowerCase());

      if (
        !selectedSector &&
        !searchTerm &&
        !groupResponsible &&
        !targetedEntity &&
        !selectedDropdownFilter &&
        !selectedRegion &&
        !cityLower &&
        !stateLower &&
        !countryLower
      ) {
        return (
          startDateMatches &&
          endDateMatches &&
          industryMatches &&
          cityMatches &&
          stateMatches &&
          countryMatches
        );
      }

      if (selectedSector) {
        const sectorMatches = sector.some((sec) =>
          sec.toLowerCase().includes(selectedSector.toLowerCase())
        );

        return (
          titleMatches &&
          sectorMatches &&
          groupResponsibleMatches &&
          targetedEntityMatches &&
          dropdownFilterMatches &&
          regionMatches &&
          cityMatches &&
          stateMatches &&
          countryMatches &&
          startDateMatches &&
          endDateMatches &&
          industryMatches
        );
      }

      return (
        titleMatches &&
        groupResponsibleMatches &&
        targetedEntityMatches &&
        dropdownFilterMatches &&
        regionMatches &&
        cityMatches &&
        stateMatches &&
        countryMatches &&
        startDateMatches &&
        endDateMatches &&
        industryMatches
      );
    });
  };

  const filteredReports = filterReports(
    reports,
    searchTerm,
    selectedSector,
    groupResponsible,
    targetedEntity,
    selectedDropdownFilter,
    startDate,
    endDate,
    selectedIndustryFilter,
    selectedRegion,
    cityFilter,
    stateFilter,
    countryFilter,
    prio3Filter,
    orgs
  );

  const openReport = (linkUrl, reportType = 'ReportsGrid') => {
    const userEmail = localStorage.getItem('user-email');
    if (!userEmail) {
      alert('You must be logged in to view this report.');
      return;
    }

    const filename = linkUrl.split('/').pop();
    const reportUrl = `/api/reports/${encodeURIComponent(filename)}?email=${encodeURIComponent(userEmail)}`;

    fetch('/api/log-report', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      keepalive: true,
      body: JSON.stringify({
        reportName: filename,
        reportType,
        userEmail,
      }),
    }).catch(() => {});

    window.open(reportUrl, '_blank');
  };

  return (
    <div className="reports-grid-shell">
      <div className="reports-grid reports-grid-main">
        <div className="reports-header reports-grid-header-sticky">
          <div className="header-item">Title</div>
          <div className="header-item">Report Date</div>
          <div className="header-item">Incident Date</div>
          <div className="header-item">Location</div>
          <div className="header-item">Sector</div>
          <div className="header-item">Report Type</div>
          <div className="header-item">PIN</div>
        </div>

        {filteredReports.map((report) => (
          <div className="report-row" key={report.fields.id}>
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                openReport(report.fields.Links1);
              }}
              className="report-item report-title"
            >
              {report.fields.Title}
            </a>

            <div className="report-item">
              {report.fields.field_1_formatted}
            </div>
            <div className="report-item">
              {report.fields.field_2_formatted}
            </div>
            <div className="report-item">
              {[report.fields.field_5, report.fields.field_4, report.fields.field_3]
                .filter(Boolean)
                .join(', ')}
            </div>
            <div className="report-item">
              {Array.isArray(report.fields.field_6)
                ? report.fields.field_6.join(', ')
                : report.fields.field_6 || ''}
            </div>

            <div className="report-item">
    {report.fields.field_9 || ''}
  </div>

 <div className="report-item report-pin">
  {report.fields.Prio3 === true && (
    <img
      src={goalIcon}
      alt="Priority"
      className="report-pin-icon"
    />
  )}
</div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ReportsGrid;
